/**
 * Web Audio API Synthesiser Service
 * Zero external audio dependencies. Generates crisp, synthesized sound effects
 * and manages the mobile audio context lifecycle cleanly.
 */

export class AudioService {
  private ctx: AudioContext | null = null;
  private isUnlocked = false;

  public unlock(): void {
    if (this.isUnlocked && this.ctx && this.ctx.state === "running") {
      return;
    }

    try {
      const AudioCtxClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AudioCtxClass) return;

      if (!this.ctx) {
        this.ctx = new AudioCtxClass();
      }

      if (this.ctx.state === "suspended") {
        void this.ctx.resume();
      }

      this.isUnlocked = true;
    } catch {
      // Audio context may be restricted by device policy
    }
  }

  public playTick(isFinal = false): void {
    this.unlock();
    if (!this.ctx || this.ctx.state !== "running") return;

    try {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();

      const now = this.ctx.currentTime;
      const duration = isFinal ? 0.35 : 0.12;
      const frequency = isFinal ? 880 : 440;
      const volume = isFinal ? 0.35 : 0.2;

      osc.type = isFinal ? "triangle" : "sine";
      osc.frequency.setValueAtTime(frequency, now);

      gain.gain.setValueAtTime(volume, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

      osc.connect(gain);
      gain.connect(this.ctx.destination);

      osc.start(now);
      osc.stop(now + duration);
    } catch {
      // Fallback silently if audio node fails
    }
  }

  public playCardFlip(): void {
    this.unlock();
    if (!this.ctx || this.ctx.state !== "running") return;

    try {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      const now = this.ctx.currentTime;
      const duration = 0.16;

      osc.type = "triangle";
      osc.frequency.setValueAtTime(180, now);
      osc.frequency.exponentialRampToValueAtTime(320, now + duration);

      gain.gain.setValueAtTime(0.3, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

      osc.connect(gain);
      gain.connect(this.ctx.destination);

      osc.start(now);
      osc.stop(now + duration);
    } catch {
      // Fallback silently
    }
  }

  public dispose(): void {
    if (this.ctx) {
      void this.ctx.close();
      this.ctx = null;
      this.isUnlocked = false;
    }
  }
}
